import React, { FC, ReactElement, useState, useEffect } from "react"
import MainLayout from "../Layout/MainLayout"
import Calendar from "../Components/BlackoutDates/Calendar"
import BlackoutList from "../Components/BlackoutDates/BlackoutList"
import CreateBlackoutModal from "../Components/BlackoutDates/CreateBlackoutModal"
import EditBlackoutModal from "../Components/BlackoutDates/EditBlackoutModal"
import {
  getBlackoutDates,
  createBlackoutDate,
  updateBlackoutDate,
  deleteBlackoutDate,
  type BlackoutDate
} from "../Api/BlackoutDateApi"

const BlackoutDates: FC = (): ReactElement => {
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const date = new Date()
    date.setDate(1)
    return date
  })
  const [blackoutDates, setBlackoutDates] = useState<BlackoutDate[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedBlackout, setSelectedBlackout] = useState<BlackoutDate | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  
  // データ取得
  useEffect(() => {
    fetchBlackoutDates()
  }, [])
  
  const fetchBlackoutDates = async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true)
      }
      const data = await getBlackoutDates()
      setBlackoutDates(data)
    } catch (error) {
      console.error('予約不可日の取得に失敗しました:', error)
      if (showLoading) {
        alert(error instanceof Error ? error.message : '予約不可日の取得に失敗しました。')
      }
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }
  
  const handleDateClick = (date: Date, blackout?: BlackoutDate) => {
    setSelectedDate(date)
    if (blackout) {
      // 既存の予約不可日がある場合は編集モーダルを開く
      setSelectedBlackout(blackout)
      setIsEditModalOpen(true)
    } else {
      // 新規登録の場合は作成モーダルを開く
      // 既存の予約不可日があるかチェック（is_activeがfalseのものも含めて）
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      const existingBlackout = blackoutDates.find(b => {
        const blackoutDate = b.date ? (b.date.includes('T') ? b.date.split('T')[0] : b.date) : ''
        return blackoutDate === dateStr
      })
      if (existingBlackout) {
        setSelectedBlackout(existingBlackout)
        setIsEditModalOpen(true)
      } else {
        setSelectedBlackout(null)
        setIsCreateModalOpen(true)
      }
    }
  }
  
  const handleCreateSave = async (data: { date: string; reason: string; is_active: boolean }) => {
    try {
      await createBlackoutDate({
        date: data.date,
        reason: data.reason,
        is_active: data.is_active
      })
      
      // データを再取得してカレンダーを更新（ローディング表示なし）
      await fetchBlackoutDates(false)
      
      // モーダルを閉じる
      setIsCreateModalOpen(false)
      setSelectedDate(null)
      setSelectedBlackout(null)
    } catch (error) {
      // エラーが発生してもモーダルを閉じる（新規登録の場合）
      setIsCreateModalOpen(false)
      setSelectedDate(null)
      setSelectedBlackout(null)
      // データを再取得してカレンダーを更新（ローディング表示なし）
      await fetchBlackoutDates(false)
      // エラーを再スローして、CreateBlackoutModalで処理させる
      throw error
    }
  }
  
  const handleUpdateSave = async (data: { date: string; reason: string; is_active: boolean }) => {
    if (!selectedBlackout) return
    
    try {
      await updateBlackoutDate(selectedBlackout.id, {
        date: data.date,
        reason: data.reason,
        is_active: data.is_active
      })
      
      // データを再取得してカレンダーを更新（ローディング表示なし）
      await fetchBlackoutDates(false)
      
      // モーダルを閉じる
      setIsEditModalOpen(false)
      setSelectedDate(null)
      setSelectedBlackout(null)
    } catch (error) {
      // エラーを再スローして、EditBlackoutModalで処理させる
      throw error
    }
  }
  
  const handleDeleteClick = (id: number) => {
    setDeleteConfirmId(id)
  }
  
  const handleDeleteConfirm = async (id: number) => {
    try {
      await deleteBlackoutDate(id)
      // データを再取得してカレンダーを更新（ローディング表示なし）
      await fetchBlackoutDates(false)
      setDeleteConfirmId(null)
      // EditBlackoutModalから呼ばれた場合はモーダルを閉じる
      if (isEditModalOpen) {
        setIsEditModalOpen(false)
        setSelectedDate(null)
        setSelectedBlackout(null)
      }
    } catch (error) {
      console.error('削除エラー:', error)
      setDeleteConfirmId(null)
      // エラーを再スローして、EditBlackoutModalで処理させる
      throw error
    }
  }
  
  const handleDeleteCancel = () => {
    setDeleteConfirmId(null)
  }
  
  const handleEditFromList = (blackout: BlackoutDate) => {
    const date = new Date(blackout.date)
    setSelectedDate(date)
    setSelectedBlackout(blackout)
    setIsEditModalOpen(true)
  }
  
  const handlePreviousMonth = () => {
    const newMonth = new Date(currentMonth)
    newMonth.setMonth(newMonth.getMonth() - 1)
    setCurrentMonth(newMonth)
  }
  
  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth)
    newMonth.setMonth(newMonth.getMonth() + 1)
    setCurrentMonth(newMonth)
  }
  
  const formatMonthTitle = (date: Date): string => {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    return `${year}年${month}月`
  }
  
  if (loading) {
    return (
      <MainLayout
        headerTitle="予約不可日設定"
        headerBreadcrumb="ホーム / カレンダー管理 / 予約不可日設定"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">読み込み中...</div>
        </div>
      </MainLayout>
    )
  }
  
  return (
    <MainLayout
      headerTitle="予約不可日設定"
      headerBreadcrumb="ホーム / カレンダー管理 / 予約不可日設定"
    >
      {/* 説明カード */}
      <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
        <div className="p-6">
          <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
            <svg className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <strong className="text-gray-900">カレンダーから予約不可日を設定できます。</strong><br />
              <span className="text-gray-700">日付をクリックして予約不可日に設定/解除してください。理由を入力することもできます。</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* カレンダーカード */}
      <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <h3 className="text-xl font-semibold text-gray-900 m-0">予約不可日カレンダー</h3>
            <div className="flex items-center gap-4">
              <button
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all"
                onClick={handlePreviousMonth}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                前月
              </button>
              <h2 className="text-xl font-semibold text-gray-900 m-0 min-w-[150px] text-center">
                {formatMonthTitle(currentMonth)}
              </h2>
              <button
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all"
                onClick={handleNextMonth}
              >
                次月
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="p-6">
          {/* カレンダー凡例 */}
          <div className="flex gap-6 mb-6 p-4 bg-gray-50 rounded-lg flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded border border-pink-500 bg-pink-100"></div>
              <span className="text-sm">予約不可日</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded border border-gray-300 bg-yellow-100"></div>
              <span className="text-sm">今日</span>
            </div>
          </div>
          
          {/* カレンダー */}
          <Calendar
            currentMonth={currentMonth}
            blackoutDates={blackoutDates}
            onDateClick={handleDateClick}
            onPreviousMonth={handlePreviousMonth}
            onNextMonth={handleNextMonth}
          />
        </div>
      </section>
      
      {/* 予約不可日一覧カード */}
      <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-900 m-0">設定済み予約不可日</h3>
        </div>
        <div className="p-6">
          <BlackoutList
            blackoutDates={blackoutDates}
            currentMonth={currentMonth}
            onEdit={handleEditFromList}
            onDelete={handleDeleteClick}
          />
        </div>
      </section>
      
      {/* 作成モーダル */}
      <CreateBlackoutModal
        isOpen={isCreateModalOpen}
        date={selectedDate}
        onClose={() => {
          setIsCreateModalOpen(false)
          setSelectedDate(null)
          setSelectedBlackout(null)
        }}
        onSave={handleCreateSave}
      />
      
      {/* 編集モーダル */}
      <EditBlackoutModal
        isOpen={isEditModalOpen}
        date={selectedDate}
        blackout={selectedBlackout}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedDate(null)
          setSelectedBlackout(null)
        }}
        onSave={handleUpdateSave}
        onDelete={handleDeleteConfirm}
      />
      
      {/* 削除確認モーダル（一覧から） */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDeleteCancel}></div>
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-[90%] p-6 animate-[modalSlideIn_0.3s_ease]">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">削除の確認</h3>
            <p className="text-gray-700 mb-6">この予約不可日を削除しますか？</p>
            <div className="flex justify-end gap-3">
              <button
                className="inline-flex items-center justify-center px-5 py-2.5 bg-gray-100 text-gray-900 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
                onClick={handleDeleteCancel}
              >
                キャンセル
              </button>
              <button
                className="inline-flex items-center justify-center px-5 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-all"
                onClick={() => deleteConfirmId && handleDeleteConfirm(deleteConfirmId)}
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
      
    </MainLayout>
  )
}

export default BlackoutDates

